import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AccountingRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', debit_account: '', credit_account: '', description: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [rRes, aRes] = await Promise.all([api.get('/accounting-rules'), api.get('/chart-of-accounts')]);
      setRules(rRes.data || []);
      setAccounts((aRes.data || []).sort((a, b) => (a.account_code || '').localeCompare(b.account_code || '')));
    } catch (e) { setRules([]); }
    setLoading(false);
  };

  const openCreate = () => { setEditId(null); setForm({ name: '', debit_account: '', credit_account: '', description: '' }); setShowModal(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ name: r.name, debit_account: r.debit_account, credit_account: r.credit_account, description: r.description || '' }); setShowModal(true); };

  const handleSubmit = async () => {
    if (!form.name || !form.debit_account || !form.credit_account) { toast.error('Name and accounts required'); return; }
    try {
      if (editId) {
        await api.put(`/accounting-rules/${editId}`, form);
        toast.success('Rule updated');
      } else {
        await api.post('/accounting-rules', form);
        toast.success('Rule created');
      }
      setShowModal(false); load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/accounting-rules/${id}`); toast.success('Rule deleted'); load(); } catch { toast.error('Failed'); }
  };

  const getAccName = (code) => { const a = accounts.find(x => x.account_code === code); return a ? `${code} - ${a.account_name}` : code; };

  return (
    <AdminSubPage title="Accounting Rules" breadcrumb={['Accounting', 'Accounting Rules']} backPath="/dashboard/accounting">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Define automatic posting rules for recurring accounting entries.</p>
        <Button onClick={openCreate} className="bg-[#1f1f1f] text-white text-sm gap-1.5" data-testid="add-rule-btn"><Plus className="w-4 h-4" /> Add Rule</Button>
      </div>
      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Rule Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Debit Account</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Credit Account</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Description</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            rules.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">No rules defined. Click "Add Rule" to create one.</td></tr> :
            rules.map(r => (
              <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`rule-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{r.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{getAccName(r.debit_account)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{getAccName(r.credit_account)}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{r.description || '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 px-2 text-xs"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} className="h-7 px-2 text-xs text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="ar-desc">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Accounting Rule</DialogTitle></DialogHeader>
          <p id="ar-desc" className="sr-only">Accounting rule form</p>
          <div className="space-y-4">
            <div><Label>Rule Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ar-name" /></div>
            <div><Label>Debit Account *</Label>
              <Select value={form.debit_account} onValueChange={v => setForm({...form, debit_account: v})}>
                <SelectTrigger data-testid="ar-debit"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.account_code} value={a.account_code}><span className="font-mono mr-2">{a.account_code}</span>{a.account_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Credit Account *</Label>
              <Select value={form.credit_account} onValueChange={v => setForm({...form, credit_account: v})}>
                <SelectTrigger data-testid="ar-credit"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.map(a => <SelectItem key={a.account_code} value={a.account_code}><span className="font-mono mr-2">{a.account_code}</span>{a.account_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="ar-submit">{editId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default AccountingRulesPage;
